"use client"

import { useActionState, useEffect, useState } from "react"
import { toast } from "sonner"
import SubmitButton from "@/components/SubmitButton"
import { Turnstile } from "@dealerzone/libs/client"
import { TURNSTILE_SITE_KEY } from "@/lib/turnstileSiteKey"
import { subscribeToNewsletter, type NewsletterFormState } from "@/lib/newsletterAction"

interface NewsletterFormProps {
  onSuccess?: () => void
}

export default function NewsletterForm({ onSuccess }: NewsletterFormProps) {
  const [turnstileReset, setTurnstileReset] = useState(0)
  const [state, formAction] = useActionState(async (prevState: NewsletterFormState, formData: FormData) => {
    const nextState = await subscribeToNewsletter(prevState, formData)
    if (!nextState?.response.success) {
      setTurnstileReset((prev) => prev + 1)
    }
    return nextState
  }, null)
  const submitted = state?.response.success === true
  const errors = state?.response.errors

  useEffect(() => {
    if (!state) {
      return
    }
    if (state.response.success) {
      if (onSuccess) {
        onSuccess()
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } else {
      const errorMessage = state.response.errors
        ? Object.values(state.response.errors).flat().join(", ")
        : state.response.message || "Failed to subscribe"
      toast.error(errorMessage)
    }
  }, [state, onSuccess])

  function inputClass(field: string) {
    const base = "mt-1 block w-full rounded-lg border px-4 py-3 text-sm focus:outline-none focus:ring-1"
    if (errors?.[field]) {
      return `${base} border-red-500 focus:border-red-500 focus:ring-red-500`
    }
    return `${base} border-gray-300 focus:border-amber-500 focus:ring-amber-500`
  }

  if (submitted) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-6">
        <h3 className="mb-2 text-lg font-semibold text-green-800">
          <i className="tabler-circle-check-filled mr-2 align-middle"></i>
          You&apos;re Subscribed!
        </h3>
        <p className="text-green-700">
          Thank you for subscribing to the Auto LifeStyle newsletter. You&apos;ll hear from us soon with new inventory, deals, and updates.
        </p>
      </div>
    )
  }

  return (
    <form action={formAction} className="space-y-5">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          defaultValue={state?.values.email}
          className={inputClass("email")}
          placeholder="your@email.com"
        />
      </div>

      <Turnstile siteKey={TURNSTILE_SITE_KEY} resetSignal={turnstileReset} />

      <p className="text-xs text-gray-500">
        By subscribing, you consent to receive marketing communications from Auto LifeStyle. You can unsubscribe at any time.
      </p>

      <SubmitButton
        label="Subscribe"
        pendingLabel="Subscribing..."
        className="w-full rounded-lg bg-amber-500 px-6 py-3 text-sm font-semibold text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
      />
    </form>
  )
}
